import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@NA/Overrides/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@NA/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory from "@NA/Overrides/Widgets/Header/SearchHistory";
import { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { ButtonIcon } from "@insite/mobius/Button";
import X from "@insite/mobius/Icons/X";
import Hidden from "@insite/mobius/Hidden";
import ADIColors from "@NA/Utils/ADIColors";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@NA/Components/ADIHeaderSearchPopover";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { isEmpty } from "lodash";
import getProp from "@insite/mobius/utilities/getProp";
import { GridWrapper } from "@insite/mobius/GridContainer";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    const {
        pages: {
            productList: { isLoading, productFilters, parameter, isSearchPage, filterQuery, productInfosByProductId },
        },
    } = state;
    const productsDataView = getProductsDataView(state, parameter);

    return {
        productsDataView,
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        toggleDrawer: state.ADIGlobalData.loginDrawerStore.loginDrawer,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    toggleLoginDrawer,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    showSearbar: boolean;
    isbanner: string | null;
    searchHistory: SearchHistoryItem[];
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                padding: 6px;
                transform: rotate(90deg);
                svg {
                    height: 24px;
                    width: 24px;
                    color: ${ADIColors.darkBlue1};
                    stroke-width: 2.5px;
                }
            `,
        },
        border: "rounded",
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                border-radius: 3px;
                font-size: 12px;
                width: 100%;
                float: right;
                height: 34px;
                padding: 9px 40px 9px 15px;
                font-weight: 600;
                border: 1px solid ${ADIColors.gray3};

                :focus-within {
                    padding: 9px 40px 9px 15px;
                    outline-color: ${ADIColors.darkBlack3};    
                    outline-style:none;
                    outline-width: 0px;
                    border: 1px solid ${ADIColors.gray3};
                    border-radius: 3px;
                }
                &:focus {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme, [null, null, null,
                        css` 
                                outline-color: ${ADIColors.darkBlack3};      
                                outline-style: none;
                                outline-width: 0px;
                                border: 1px solid ${ADIColors.gray3};
                                border-radius: 3px;
                                `,],
                        "min",);
                    }}                  
                }
                &:focus-visible {
                    ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme, [null, null, null,
                        css` 
                            outline-color: ${ADIColors.darkBlack3};   
                            outline-style: ${getProp("theme.focus.style", "solid")};
                            outline-width: 1px;
                            outline-offset: 1px;
                            border: 1px solid ${ADIColors.gray3}; 
                            border-radius: 3px;`,],
                        "min",);
                }}
                } 
                ::placeholder {
                    color: ${ADIColors.gray3};
                    opacity: 1;
                }

                ::-ms-input-placeholder {
                    color: ${ADIColors.gray3};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 300px;
                        `,
                        css`
                            max-width: 400px;
                        `,
                    ])}
            `,
            formInputWrapper: css`
                display: flex;
                > button {
                    width: 35px !important;
                    height: 35px !important;
                    padding: 4px !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                                right: 0px !important;
                            `,
                        css`
                                right: 0px !important;
                            `,
                        null,
                    ])}
                    > ${IconWrapper} {
                        top: 0px;
                        right: 0px;
                        width: 100%;
                        height: 100%;
                        padding: 4px;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            max-width: 338.63px;
                        `,
                    ])}
                @media only screen and (max-width: 1700px) {
                    max-width: 400px;
                }
                @media only screen and (min-width: 1700px) {
                    max-width: 400px;
                }
            `,
        },
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            position: absolute;
            display: flex;
            font-size: 12px;
            font-stretch: 100%;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
            background: ${getColor("common.background")};
            top: 0;
            box-shadow: none;
            cursor: default;
            max-height: 513px !important;
            min-height: auto !important;
            width: 100%;
            right: 0 !important;
            left: 0 !important;
            border-radius: 3px;
            height: 100% !important;
            background: transparent;
            margin: auto;
            overflow: hidden;
            > div[class^="sc"] {
                overflow: hidden auto;
                max-height: inherit !important;
                height: auto !important;
                min-height: auto !important;
                display: flex;
                border-radius: 3px;
                border: solid 1px ${getColor("common.disabled")};
                padding: 35px 15px 20px 15px;
                flex-direction: column;
                flex: 1;
                max-width: calc(100% - 450px);
                right: 15px;
                left: auto;
                position: absolute;
                z-index: 11111;
                background: ${ADIColors.white};;
                width: 100%;
                float: right;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px 15px 20px 15px;
                            max-width: 100%;
                            right: 0px;
                        `,
                        css`
                            padding: 15px 15px 20px 15px;
                            max-width: 100%;
                            right: 0px;
                        `,
                        css`
                            padding: 25px 20px 20px 20px;
                            max-width: 100%;
                            right: 0px;
                        `,
                    ],
                )}

                &::-webkit-scrollbar {
                    width: 4px;
                }

                &::-webkit-scrollbar-track {
                    background: ${getColor("common.background")};
                }

                &::-webkit-scrollbar-thumb {
                    background-color: ${getColor("common.border")};
                    border-radius: 3px;
                }

                button {
                    margin: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 0px !important;
                        padding: 0px !important;
                        top: 83px;
                        left: 0px !important;
                        max-height: 100% !important;
                        height: 100%;
                    `,
                    css`
                        right: 0px !important;
                        padding: 0px !important;
                        top: 83px !important;
                        left: 0px !important;
                        max-height: 100% !important;
                        height: 100%;
                    `,
                    css`
                        right: 0px !important;
                        padding: 0px !important;
                        top: 78px !important;
                        left: 0px !important;
                        max-height: 100% !important;
                        height: 100%;
                    `,
                    css`
                        max-width: 1470px !important;
                        right: 0px !important;
                        left: 0px !important;
                        height: 100% !important;
                        top: 97px !important;
                    `,
                    css`
                        max-width: 1470px !important;
                        width: auto !important;
                        height: 100% !important;
                        top: 97px !important;
                    `,
                ])}
        `,
    },

    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            position: relative;
            padding: 10px;
            width: 100px;
            min-width: 0px !important;
            max-width: 100%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            gap: 15px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 15px;
            flex: 1;
            width: 100%;
            border-bottom: 0px solid ${getColor("common.border")};

            > span, > button {
                max-width: max-content;
                span {
                    cursor: pointer;
                }
            }
            &:last-child:not(:only-child) {
                border-left: 1px solid ${getColor("common.border")};
                width: 50%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border-width: 1px;
                        padding: 0 0 10px;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                            border: none;
                        }
                    `,
                    css`
                        border-width: 1px;
                        padding: 0 0 10px;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                            border: none;
                            > ${GridWrapper} {
                                max-width: 100%;
                            }
                        }
                    `,
                    css`
                        border: none;
                        padding: 0px;
                        width: 48%;
                        &:last-child:not(:only-child) {
                            width: 50%;
                            flex: unset;
                            padding: 0 0 0 20px;
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        width: 50%;
                        &:last-child:not(:only-child) {
                            width: 50%;
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        width: 50%;
                        &:last-child:not(:only-child) {
                            width: 50%;
                        }
                    `,
                ])}
        `,
    },

    autocompleteProductsStyles: {
        headerText: {
            css: css`
                color: ${getColor("text.accent")} !important;
                font-size: 14px !important;
                font-style: normal;
                font-weight: 700 !important;
                line-height: normal !important;
            `,
        },
        container: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
                margin-bottom: 10px;
                height: 100%;
                &:last-child {
                    border-bottom: none;
                }
            `,
            offsetCss: css`
                margin-bottom: 8px;
            `,
        },
    },
    autocompleteCategoriesStyles: {
        headerText: {
            css: css`
                padding-top: 20px;
                color: ${getColor("text.accent")} !important;
                font-size: 14px !important;
                font-style: normal;
                font-weight: 700 !important;
                line-height: normal !important;
                padding-bottom: 6px !important;

                &:first-child {
                    padding-top: 0px;
                }
            `,
        },
        categoryItemText: {
            css: css`
                width: auto;
            `,
        },
        link: {
            css: css`
                max-width: max-content;
                > ${TypographyStyle} {
                    color: ${getColor("text.main")} !important;
                    font-weight: normal;
                    font-size: 14px;
                    line-height: normal !important;
                    padding-bottom: 6px;
                    margin: 0;
                    white-space: normal;
                    &:hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            css: css`
                padding-top: 26px !important;
                color: ${getColor("text.accent")} !important;
                font-size: 14px !important;
                font-style: normal;
                font-weight: 700 !important;
                line-height: normal !important;
                padding-bottom: 6px !important;
            `,
        },
        link: {
            css: css`
                > ${TypographyStyle} {
                    color: ${getColor("text.main")} !important;
                    font-weight: normal;
                    font-size: 14px;
                    line-height: normal !important;
                    padding-bottom: 6px;
                    margin: 0;
                    white-space: normal;
                    &:hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            css: css`
                color: ${getColor("text.accent")} !important;
                font-size: 14px !important;
                font-style: normal;
                font-weight: 700 !important;
                line-height: normal !important;
                padding-bottom: 6px !important;
            `,
        },
        brandItemText: {
            css: css`
                width: auto;
            `,
        },
        link: {
            css: css`
                > ${TypographyStyle} {
                    color: ${getColor("text.main")} !important;
                    font-weight: normal;
                    font-size: 14px;
                    line-height: normal !important;
                    padding-bottom: 6px;
                    margin: 0;
                    &:hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
};

const styles = searchInputStyles;

const SearchInputWrapper = styled.div<any>`
    display: flex;
    align-items: center;
    position: ${({ showSearbar }) => (showSearbar ? "fixed" : "absolute")};
    width: ${({ showSearbar }) => (showSearbar ? "100%" : "auto")};
    left: ${({ showSearchBar }) => (showSearchBar ? "0px" : "auto")};
    right: ${({ showSearchBar }) => (showSearchBar ? "15px" : "0")};
    bottom: ${({ showSearchBar }) => (showSearchBar ? "0px" : "5px")};
    padding: ${({ showSearbar }) => (showSearbar ? "15px" : "0px")};
    top: ${({ showSearbar, isbanner }) => (showSearbar ? isbanner === null ? "117px" : isbanner === "close" ? "31px" : "0px" : "0px")};
    height: ${({ showSearbar }) => (showSearbar ? "61px" : "auto")};
    z-index: ${({ showSearbar }) => (showSearbar ? "1200" : "0")};
    gap: ${({ showSearbar }) => (showSearbar ? "10px" : "0px")};
    background-color: ${ADIColors.white};
`;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        this.state = {
            query: "",
            focusableAutocompleteItems: [],
            isLoading: false,
            showSearbar: false,
            isbanner: null,
            searchHistory: getSearchHistory(),
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<HTMLUListElement>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                .concat(categories || [])
                .concat(brands || [])
                .concat(content || [])
            : (categories || [])
                .concat(brands || [])
                .concat(content || [])
                .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {

            const encodedQuery = encodeURIComponent(this.state.query);
           
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
        }

        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }

        this.setState({ query: "", searchHistory: getSearchHistory() });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                    });
                    this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.showAutocomplete = true;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    openAutocomplete = () => {
        const searchHistory = getSearchHistory();
        if (this.state.query !== "") {
            this.popover.current && (this.popover.current! as any).openPopover();
        }
        if (this.state.autoCompleteModel || (searchHistory.length > 0 && this.state.query === "")) {
            this.setState({ searchHistory: searchHistory });
            this.showAutocomplete = true;
             this.popover.current && (this.popover.current! as any).openPopover();           
        } else {
            this.showAutocomplete = false;
            // this.closeAutocomplete(); 
        }      
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;
            if (window.innerWidth <= 767) {
                positionStyle.top += 25;
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.setState({ showSearbar: !this.state.showSearbar });
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        const newUrl = url; // Define the new URL

        if (this.props.location.pathname === newUrl) {
            // If the new URL is the same as the current one, add a query parameter to force navigation
            this.props.history.replace({
                pathname: newUrl,
                search: `#criteria=${newUrl}` // Adding a dummy query parameter to force a refresh
            } as any);
        } else {
            this.props.history.replace(newUrl); // Replace the current URL with the new one
        }
        // this.props.history.replace(url);
    };

    closeSignInDrawer = () => {
        if (this.props.toggleDrawer) {
            this.props.toggleLoginDrawer({ loginDrawer: false });
            const headerSearch = document.getElementById("headerSearch");
            setTimeout(() => {
                if (headerSearch) {
                    headerSearch.focus();
                }
            }, 500);           
        }
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;

        return (
            <StyledWrapper {...this.styles.autocompleteWrapper}>
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                        <StyledWrapper {...this.styles.autocompleteColumnWrapper}>
                            <AutocompleteBrands
                                brands={brands}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={url => this.goToUrl(`/Catalog${url}`)}
                                extendedStyles={this.styles.autocompleteBrandsStyles}
                            />
                            <AutocompleteCategories
                                categories={categories}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                extendedStyles={this.styles.autocompleteCategoriesStyles}
                            />

                            <AutocompleteContent
                                content={content}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                extendedStyles={this.styles.autocompleteContentStyles}
                            />

                            <Hidden above="sm">
                                {products && products.length > 0 && (
                                    <StyledWrapper {...this.styles.autocompleteColumnWrapper}>
                                        <AutocompleteProducts
                                            products={products}
                                            url={this.state.query}
                                            totalCount={this.state.autoCompleteModel.properties.totalProductCount}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteProductsStyles}
                                        />
                                    </StyledWrapper>
                                )}
                            </Hidden>

                        </StyledWrapper>
                    )}
                {products && products.length > 0 && (
                    <StyledWrapper {...this.styles.autocompleteColumnWrapper}>
                        <AutocompleteProducts
                            products={products}
                            url={this.state.query}
                            totalCount={this.state.autoCompleteModel.properties.totalProductCount}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteProductsStyles}
                        />
                    </StyledWrapper>
                )}

            </StyledWrapper>
        );
    };

    handleSearchbar = () => {
        this.setState({ showSearbar: !this.state.showSearbar });
        this.closeAutocomplete();
        const isAppBanner = localStorage.getItem('bannerclosebutton');
        this.setState({ isbanner: isAppBanner });
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch`;
        const searchInput = (
            <>
                <Hidden below="md" style={{ width: "100%" }}>
                    <TextField
                        {...styles.input}
                        ref={this.props.inputRef}
                        iconClickableProps={{ onClick: this.doSearch }}
                        value={this.state.query}
                        onChange={this.handleInputChange}
                        placeholder={translate("Search by product, brand or keyword...")}
                        autoComplete="off"
                        clickableText={translate("submit search")}
                        id={searchId}
                        data-test-selector="headerSearchInputTextField"
                        onClick={this.openAutocomplete}
                    />
                </Hidden>
                <Hidden above="sm">
                    <SearchInputWrapper showSearbar={this.state.showSearbar} isbanner={this.state.isbanner}>
                        {this.state.showSearbar && (
                            <TextField
                                {...styles.input}
                                ref={this.props.inputRef}
                                iconClickableProps={{ onClick: this.doSearch }}
                                value={this.state.query}
                                onChange={this.handleInputChange}
                                autoComplete="off"
                                clickableText={translate("submit search")}
                                id={searchId}
                                data-test-selector="headerSearchInputTextField"
                                onClick={this.openAutocomplete}
                            />
                        )}
                        <ButtonIcon
                            color={ADIColors.darkBlue1}
                            size={this.state.showSearbar ? 35 : 30}
                            src={this.state.showSearbar ? X : Search}
                            onClick={() => this.handleSearchbar()}
                        />
                    </SearchInputWrapper>
                </Hidden>
            </>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    onOpen={this.closeSignInDrawer}
                >
                    {(!this.state.query || this.state.query === "") &&
                        this.state.searchHistory.length > 0 && this.showAutocomplete && (
                            <SearchHistory
                                focusedQuery={this.state.focusedSearchHistoryQuery}
                                goToUrl={this.goToUrl}
                                extendedStyles={styles.searchHistoryStyles}
                                clearHistory={this.openAutocomplete}
                            />
                        )}
                    {this.state.query && this.renderAutocomplete()}
                </Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
